import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Section, StatusPill, EmptyState } from "@/components/app/Primitives";
import { ReviewConfirmDialog } from "@/components/app/ReviewConfirmDialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export default function PurchaseOrders() {
  const { user } = useAuth();
  const [pos, setPOs] = useState([]);
  const load = () => api.get("/purchase-orders").then((r) => setPOs(r.data));
  useEffect(() => { load(); }, []);

  const accept = async (id) => { await api.post(`/purchase-orders/${id}/accept`); toast.success("PO accepted"); load(); };

  if (pos.length === 0) return <EmptyState title="No purchase orders yet" hint="POs from PROPAY GLOBAL PRIVATE LIMITED will appear here." />;

  return (
    <div className="space-y-6">
      <Section title={user.role === "influencer" ? "Your Purchase Orders" : "Back-to-back Purchase Orders"} subtitle="Issued by PROPAY GLOBAL PRIVATE LIMITED under the one-vendor model." />
      <div className="grid gap-4">
        {pos.map((po) => (
          <div key={po.id} className="bg-white rounded-xl border p-5">
            <div className="flex justify-between items-start flex-wrap gap-3">
              <div>
                <div className="text-xs font-bold uppercase tracking-widest text-[#0052FF]">{po.issued_by_entity}</div>
                <div className="font-heading font-bold text-xl text-slate-900">{po.po_number}</div>
                <div className="text-sm text-slate-600">{po.campaign} · {po.product || ""}</div>
                {po.tenant_name && (
                  <div className="mt-1 inline-flex items-center gap-1.5 text-xs bg-slate-100 text-slate-700 px-2 py-1 rounded">
                    <span className="text-slate-500">On behalf of</span>
                    <b className="text-slate-900" data-testid={`po-fmcg-${po.id}`}>{po.tenant_name}</b>
                  </div>
                )}
              </div>
              <StatusPill status={po.status} />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-4 text-sm">
              <div><div className="text-slate-500 text-xs">Amount</div><div className="font-semibold">{po.currency} {Number(po.amount).toLocaleString()}</div></div>
              <div><div className="text-slate-500 text-xs">Terms</div><div>{po.payment_terms_days} days</div></div>
              <div><div className="text-slate-500 text-xs">Issued to</div><div>{po.influencer_name}</div></div>
              <div><div className="text-slate-500 text-xs">End customer</div><div className="font-semibold">{po.tenant_name || "—"}</div></div>
            </div>
            <div className="mt-3 text-xs text-slate-500 border-t pt-3">
              <b>Deliverables:</b> {(po.services || []).map((s) => `${s.type} ×${s.count}`).join(", ")}
            </div>
            {user.role === "influencer" && po.status === "issued" && (
              <div className="mt-4">
                <ReviewConfirmDialog
                  trigger={<Button className="bg-[#0052FF] hover:bg-[#0043D9]">Accept PO</Button>}
                  title="Accept this Purchase Order?"
                  subtitle="By accepting you agree to deliver the services listed below within the payment terms. Payment will be released after every line is approved by the FMCG customer (advances may apply)."
                  details={{
                    "Issued by": po.issued_by_entity,
                    "On behalf of (End customer)": po.tenant_name || "—",
                    "PO number": po.po_number,
                    Campaign: po.campaign,
                    Amount: `${po.currency} ${Number(po.amount).toLocaleString()}`,
                    Terms: `${po.payment_terms_days} days`,
                    Lines: (po.services || []).map((s) => `${s.type} × ${s.count}`).join(", ") || "—",
                    GSTIN: po.issued_by_gstin,
                  }}
                  confirmLabel="Yes, accept"
                  confirmTone="primary"
                  onConfirm={() => accept(po.id)}
                  testIdOpen={`accept-po-${po.id}`}
                  testIdConfirm={`confirm-accept-po-${po.id}`}
                />
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
